import React, { useState, useEffect, useRef } from "react";
// Neutral grey placeholder (no running-man, no dead remote service).
import { GREY_PLACEHOLDER as defaultImage } from "../../constants/imageFallback";
import {
  // FaEnvelope, // Commented out - not used anymore
  // FaMapMarkerAlt, // Commented out - not used anymore
  FaUser,
  // FaPhone, // Commented out - not used anymore
  // FaTimes, // Commented out - not used anymore
  // FaEllipsisH, // Commented out - not used anymore
} from "react-icons/fa";
import CustomInput from "../common/CustomInput";
import CustomSelect from "../common/CustomSelect";
// import CustomPhoneInput from "../common/CustomPhoneInput"; // Commented out - not used anymore
import ImageUploadModal from "./ImageUploadModal";
import ProfileCard from "./ProfileCard";
import { tp_small } from "../../constants/icons";
import ChangePasswordModal from "../common/ChangePasswordModal";
import PhoneNumberField from "./common/PhoneNumberField";
import MobileNumberUpdateModal from "../common/MobileNumberUpdateModal";

const AccountInformation = ({
  clubProfileData,
  formApiData,
  isEditing,
  isLoading,
  isSaving,
  error,
  onApiInputChange,
  onClubDocumentChange,
  onClubLogoChange,
  onSave,
  onCancel,
  onEdit,
  onMobileUpdate,
}) => {
  const [showClubLogoUpload, setShowClubLogoUpload] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const objectUrlRef = useRef(null);

  // Handle input changes for form fields
  const handleInputChange = (field, value) => {
    // Map local form fields to API fields
    const apiFieldMap = {
      // Commented out - Club cannot update these fields:
      // phoneNumber: "mobile",
      // clubName: "club_name",
      // Only these fields can be updated by club admin:
      firstName: "first_name",
      lastName: "last_name",
      position: "admin_position",
      licenseNumber: "license_number",
    };

    if (apiFieldMap[field] && isEditing) {
      onApiInputChange(apiFieldMap[field], value);
    }
  };

  const handleMobileUpdateSuccess = (newMobile) => {
    if (typeof onMobileUpdate === "function") {
      onMobileUpdate(newMobile);
    }
  };

  const handleClubLogoUpload = () => {
    setShowClubLogoUpload(true);
  };

  const handleClubLogoUploadComplete = async (croppedImageUrl) => {
    setShowClubLogoUpload(false);
    // Convert the blob URL to a File object for API upload
    try {
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], "club_logo.jpg", { type: "image/jpeg" });
      onClubLogoChange(file);
    } catch (error) {
      console.error("Error converting cropped image to file:", error);
    }
  };

  const handleRemoveClubLogo = () => {
    onClubLogoChange(null);
    console.log("Club logo removed - reset to default");
  };

  // Helper function to get image source
  const getClubLogoSrc = () => {
    // Clean up previous object URL
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (formApiData.club_logo instanceof File) {
      // If it's a File object, create a URL for preview
      objectUrlRef.current = URL.createObjectURL(formApiData.club_logo);
      return objectUrlRef.current;
    } else if (
      typeof formApiData.club_logo === "string" &&
      formApiData.club_logo
    ) {
      // If it's a string URL
      return formApiData.club_logo;
    } else {
      // Default placeholder (local bundled asset)
      return defaultImage;
    }
  };

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="text-gray-500 text-lg">Loading profile...</span>
      </div>
    );
  }

  // Use the centralized full role list so new titles (President, Director of Football,
  // Chief Scout, etc.) appear here automatically.
  // Lazy import to avoid module cycles in this large component.
  const { STAFF_ROLE_OPTIONS } = require("../../constants/staffRoles");
  const positions = STAFF_ROLE_OPTIONS;

  return (
    <div className="sm:px-14 px-4 mx-auto p-6 space-y-8">
      {/* Club Details Section */}
      <div className="bg-white p-6 border border-[#E6E6E6]">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6">
          <h2 className="text-[11px] font-bold text-[#3C3C3C] self-start">CLUB DETAILS</h2>

          <div className="self-end"> 
          {!isEditing && (
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowChangePasswordModal(true)}
                className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
              >
                Change password
              </button>
              <button
                onClick={onEdit}
                className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
              >
                Edit profile
              </button>
            </div>
          )}
          {isEditing && (
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                disabled={isSaving}
                className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isSaving}
                className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
              >
                {isSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          )}
          </div>
        </div>

        {/* Error message */}
        {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

        <div className="flex items-center gap-6 mb-6">
          {/* Club Logo */}
          <div className="flex-shrink-0 relative">
            <div className="w-24 h-24 rounded-full bg-blue-600 flex items-center justify-center overflow-hidden">
              <img
                src={getClubLogoSrc()}
                alt="Club Logo"
                className="w-full h-full object-cover"
              />
              <div className="absolute z-10 bottom-0 right-0 w-7 h-7 rounded-sm flex items-center justify-center">
                <img
                  src={tp_small}
                  alt="tp_small"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>

          {/* Club Logo Actions (only in edit mode) */}
          {isEditing && (
            <div className="flex gap-3 justify-center items-center">
              <button
                onClick={handleClubLogoUpload}
                className="text-[#2283FF] px-4 py-2 rounded-lg bg-[#2283FF0D] hover:text-[#2283FF] text-sm font-medium"
              >
                Replace
              </button>
              {(formApiData.club_logo instanceof File ||
                (typeof formApiData.club_logo === "string" &&
                  formApiData.club_logo)) && (
                <button
                  onClick={handleRemoveClubLogo}
                  className="text-red-500 px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-sm font-medium"
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-4 mt-4 flex-col">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomInput
              label="Club name"
              value={formApiData.club_name}
              onChange={(e) => handleInputChange("clubName", e.target.value)}
              placeholder="Enter club name"
              disabled={true} // Always disabled - club cannot update club name
            />
          </div>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <PhoneNumberField
              label="Phone number"
              value={formApiData.mobile}
              placeholder="294 27 84 11 00"
              disabled
              showEdit={isEditing}
              onEdit={() => setShowMobileModal(true)}
            />
          </div>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomInput
              label="License number"
              value={formApiData.license_number}
              onChange={(e) =>
                handleInputChange("licenseNumber", e.target.value)
              }
              placeholder="XX XX XXXX XXXX XX"
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>

      {/* Admin Details Section */}
      <div className="bg-white p-6 border border-[#E6E6E6]">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-[11px] font-bold text-[#3C3C3C]">
            ADMIN DETAILS
          </h2>
        </div>

        <div className="flex items-start gap-4 flex-col">

          {/* Admin Form Fields */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
            <CustomInput
              label="First name"
              value={formApiData.first_name}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              placeholder="Enter first name"
              disabled={!isEditing}
            />
            <CustomInput
              label="Last name"
              value={formApiData.last_name}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              placeholder="Enter last name"
              disabled={!isEditing}
            />
          </div>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          <CustomSelect
            label="Position"
            value={
              // Convert label to value for dropdown
              (positions || []).find(p => p.label === formApiData.admin_position)?.value ||
              (positions || []).find(p => p.value === formApiData.admin_position)?.value ||
              formApiData.admin_position
            }
            onChange={(e) => {
              const selectedPosition = (positions || []).find(p => p.value === e.target.value);
              // Persist the human-readable label when known, otherwise whatever value we got
              handleInputChange("position", selectedPosition?.label || e.target.value);
            }}
            options={positions}
            className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
            placeholder="Select position"
            disabled={!isEditing}
          />
          </div>
        </div>
      </div>

      {/* Profile Card Section - Using the reusable component */}
      <ProfileCard disabled={isEditing} />

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
          onSuccess={() => {
            // Could add any success handling here
          }}
        />
      )}

      <MobileNumberUpdateModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        initialMobile={formApiData.mobile || ""}
        onSuccess={handleMobileUpdateSuccess}
      />

      {/* Club Logo Upload Modal */}
      {showClubLogoUpload && (
        <ImageUploadModal
          onClose={() => setShowClubLogoUpload(false)}
          onUpload={handleClubLogoUploadComplete}
        />
      )}
    </div>
  );
};

export default AccountInformation;
